import React, { useEffect, useState } from 'react'
import InboxIcon from '@mui/icons-material/Inbox'
import DashboardIcon from '@mui/icons-material/Dashboard'
import SettingsIcon from '@mui/icons-material/Settings'
import StorefrontIcon from '@mui/icons-material/Storefront'
import CategoryIcon from '@mui/icons-material/Category'
import DiscountIcon from '@mui/icons-material/Discount'
import AutoStoriesIcon from '@mui/icons-material/AutoStories'
import UserIcon from '@mui/icons-material/People'
import EmailIcon from '@mui/icons-material/EmailRounded'
import BarChartIcon from '@mui/icons-material/BarChart'
import NotificationsIcon from '@mui/icons-material/Notifications'
import PaymentsIcon from '@mui/icons-material/Payments'
import KeyIcon from '@mui/icons-material/Key'
import TuneIcon from '@mui/icons-material/Tune'
import MenuIcon from '@mui/icons-material/Menu'
import WebIcon from '@mui/icons-material/Web'
import useTranslation from '../../hooks/useTranslation'
import routeMapping from '../../config/constants'
import { OrderApi } from '../../api/orderApi'

const useMenuConfig = () => {
  const { t } = useTranslation()
  const [counts, setCounts] = useState({ all: null, fulfilled: null, unfulfilled: null, paid: null, unpaid: null })

  useEffect(() => {
    let mounted = true
    OrderApi.getOrderStats()
      .then(res => {
        if (mounted) setCounts(res.data || {})
      })
      .catch(() => {})
    return () => { mounted = false }
  }, [])

  const withCount = (label, n) => (typeof n === 'number' ? `${label} (${n})` : label)

  const menuConfig = [
    {
      icon: <DashboardIcon />,
      title: t('labels.menu.dashboard'),
      to: '/admin',
      exact: true,
      extraLinks: ['/admin/dashboard'],
      items: []
    },
    {
      icon: <InboxIcon />,
      title: t('labels.menu.orders'),
      open: false,
      items: [
        {
          title: withCount(t('labels.menu.all_orders'), counts.all),
          to: '/admin/orders/all',
          extraLinks: ['/admin/orders/all'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Orders
          }
        },
        {
          title: withCount(t('labels.menu.fulfilled_orders'), counts.fulfilled),
          to: '/admin/orders/fulfilled',
          extraLinks: ['/admin/orders/fulfilled'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Orders
          }
        },
        {
          title: withCount(t('labels.menu.unfulfilled_orders'), counts.unfulfilled),
          to: '/admin/orders/unfulfilled',
          extraLinks: ['/admin/orders/unfulfilled'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Orders
          }
        },
        {
          title: withCount(t('labels.menu.paid_orders'), counts.paid),
          to: '/admin/orders/paid',
          extraLinks: ['/admin/orders/paid'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Orders
          }
        },
        {
          title: withCount(t('labels.menu.unpaid_orders'), counts.unpaid),
          to: '/admin/orders/unpaid',
          extraLinks: ['/admin/orders/unpaid'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Orders
          }
        }
      ]
    },
    {
      icon: <CategoryIcon />,
      title: t('labels.menu.products'),
      open: false,
      items: [
        {
          title: t('labels.menu.products'),
          to: '/admin/products',
          extraLinks: ['/admin/products/'],
          excludeLinks: ['/admin/products/inventory'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Products
          }
        },
        {
          title: t('labels.menu.inventory'),
          to: '/admin/inventory',
          extraLinks: ['/admin/inventory/'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Inventory
          },
        },
        {
          title: t('labels.menu.categories'),
          to: '/admin/categories',
          extraLinks: ['/admin/categories/'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Categories
          },
        },
        {
          title: t('labels.menu.collections'),
          to: '/admin/collections',
          extraLinks: ['/admin/collections/'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Collections
          },
        }
      ]
    },
    {
      icon: <DiscountIcon />,
      title: t('labels.menu.discounts'),
      to: '/admin/discounts',
      ability: {
        action: routeMapping.actions.list,
        controller: routeMapping.controllers.Discounts
      },
    },
    {
      icon: <UserIcon />,
      title: t('labels.menu.users'),
      open: false,
      items: [
        {
          title: t('labels.menu.users'),
          to: '/admin/users',
          extraLinks: ['/admin/users/'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Users
          }
        },
        {
          title: t('labels.menu.customers'),
          to: '/admin/customers',
          extraLinks: ['/admin/customers/'],
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Users
          }
        },
        {
          title: t('labels.menu.user_groups'),
          to: '/admin/userGroups',
          extraLinks: ['/admin/userGroups/'],
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Discounts
          },
        }
      ]
    },
    {
      icon: <AutoStoriesIcon />,
      title: t('labels.menu.pages'),
      to: '/admin/pages',
      ability: {
        action: routeMapping.actions.list,
        controller: routeMapping.controllers.Discounts
      },
    },
    {
      icon: <BarChartIcon />,
      title: t('labels.menu.report'),
      open: false,
      items: [
        {
          title: t('labels.menu.sales_by_product'),
          to: '/admin/reports/sales-by-product',
          extraLinks: ['/admin/reports/sales-by-product/'],
          active: false,
        }
      ]
    },
    {
      icon: <NotificationsIcon />,
      title: t('labels.notifications'),
      to: '/admin/notifications',
      ability: {
        action: routeMapping.actions.list,
        controller: routeMapping.controllers.Notifications
      },
    },
    {
      icon: <EmailIcon />,
      title: t('labels.menu.email_menu'),
      open: false,
      items: [
        {
          title: t('labels.menu.email_template'),
          to: '/admin/email-templates',
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.EmailTemplates
          }
        },
        {
          title: t('labels.menu.email_campaigns'),
          to: '/admin/email-campaigns',
          active: false,
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.EmailTemplates
          }
        }
      ]
    },
    {
      icon: <StorefrontIcon />,
      title: 'Storefront',
      open: false,
      items: [
        {
          title: 'Themes',
          to: '/admin/themes',
          extraLinks: ['/admin/themes/'],
          active: false
        },
        {
          title: t('labels.menu.menus'),
          to: '/admin/menus',
          extraLinks: ['/admin/menus/'],
          ability: {
            action: routeMapping.actions.list,
            controller: routeMapping.controllers.Menus
          },
          active: false
        },
        {
          title: t('labels.menu.homepage'),
          to: '/admin/frontpage',
          extraLinks: ['/admin/frontpage/'],
          active: false
        }
      ]
    },
    {
      icon: <SettingsIcon />,
      title: t('labels.menu.settings'),
      to: '/admin/store',
      extraLinks: ['/admin/store'],
      ability: {
        action: routeMapping.actions.retrieve,
        controller: routeMapping.controllers.Stores
      },
      items: []
    }
  ]

  return menuConfig
}

export default useMenuConfig
